import { useEffect, useState } from "react";
import api from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter,
} from "@/components/ui/dialog";
import { Badge } from "@/components/ui/badge";
import { toast } from "sonner";
import { Play, Square, Clock3, FileDown, FileText, Plus, Pencil, Trash2 } from "lucide-react";
import { DASH } from "@/constants/ui";

const MONTHS_DE = ["Januar","Februar","März","April","Mai","Juni","Juli","August","September","Oktober","November","Dezember"];

function elapsedSince(iso) {
  const start = new Date(iso).getTime();
  const diff = Math.max(0, Date.now() - start);
  const h = Math.floor(diff / 3600000);
  const m = Math.floor((diff % 3600000) / 60000);
  const s = Math.floor((diff % 60000) / 1000);
  return `${String(h).padStart(2, "0")}:${String(m).padStart(2, "0")}:${String(s).padStart(2, "0")}`;
}
function fmtDuration(min) {
  const h = Math.floor(min / 60); const m = min % 60;
  return `${String(h).padStart(2, "0")}h ${String(m).padStart(2, "0")}m`;
}
function fmtDate(iso) { return new Date(iso).toLocaleDateString("de-DE"); }
function fmtTime(iso) { return new Date(iso).toLocaleTimeString("de-DE", { hour: "2-digit", minute: "2-digit" }); }

export default function TimeTracking() {
  const { user } = useAuth();
  const [current, setCurrent] = useState(null);
  const [entries, setEntries] = useState([]);
  const [employees, setEmployees] = useState([]);
  const [targetId, setTargetId] = useState(user.id);
  const [tick, setTick] = useState(0);

  const reload = async () => {
    const c = await api.get("/time/current"); setCurrent(c.data);
    const e = await api.get("/time/entries", { params: { user_id: targetId, days: 60 } });
    setEntries(e.data);
  };

  useEffect(() => { reload(); /* eslint-disable-next-line */ }, [targetId]);
  useEffect(() => {
    if (user.role === "admin") api.get("/users").then(({ data }) => setEmployees(data)).catch(() => {});
  }, [user.role]);

  useEffect(() => {
    if (!current) return;
    const id = setInterval(() => setTick((t) => t + 1), 1000);
    return () => clearInterval(id);
  }, [current]);

  const clockIn = async () => {
    try { await api.post("/time/clock-in"); toast.success("Eingestempelt"); reload(); }
    catch (e) { toast.error(e.response?.data?.detail || "Fehler"); }
  };
  const clockOut = async () => {
    try { await api.post("/time/clock-out"); toast.success("Ausgestempelt"); reload(); }
    catch (e) { toast.error(e.response?.data?.detail || "Fehler"); }
  };

  const today = new Date();
  const [reportYear, setReportYear] = useState(today.getFullYear());
  const [reportMonth, setReportMonth] = useState(today.getMonth() + 1);
  const [downloading, setDownloading] = useState("");

  const downloadBlob = async (path, params, filenameFallback) => {
    setDownloading(path);
    try {
      const res = await api.get(path, { params, responseType: "blob" });
      const cd = res.headers["content-disposition"] || "";
      const match = /filename="([^"]+)"/.exec(cd);
      const fname = match ? match[1] : filenameFallback;
      const url = URL.createObjectURL(new Blob([res.data], { type: res.headers["content-type"] }));
      const a = document.createElement("a");
      a.href = url; a.download = fname; document.body.appendChild(a); a.click(); a.remove();
      URL.revokeObjectURL(url);
      toast.success("Download gestartet");
    } catch (e) {
      toast.error("Fehler beim Download");
    } finally {
      setDownloading("");
    }
  };

  const exportCsv = () => downloadBlob("/time/export", { user_id: targetId, year: reportYear, month: reportMonth }, "zeiterfassung.csv");
  const exportPdf = () => downloadBlob("/time/report", { user_id: targetId, year: reportYear, month: reportMonth }, "stundenreport.pdf");

  const totalMin = entries.filter((e) => e.end).reduce((a, b) => a + (b.duration_minutes || 0), 0);

  // Group by date
  const grouped = entries.reduce((acc, e) => {
    const d = e.start?.split("T")[0] || "—";
    (acc[d] = acc[d] || []).push(e);
    return acc;
  }, {});

  void tick;

  return (
    <div className="space-y-6">
      <div>
        <p className="ph-label">Stempeluhr</p>
        <h1 className="font-display text-3xl font-semibold tracking-tight mt-1">Zeiterfassung</h1>
        <p className="text-slate-500 text-sm mt-1">
          Stempel dich ein und aus — oder trage deine Arbeitszeiten manuell ein (z.&nbsp;B. „Montag 9–16 Uhr").
        </p>
      </div>

      <div className="ph-card p-6 md:p-8 flex flex-col md:flex-row items-start md:items-center gap-6">
        <div className={`h-16 w-16 rounded-xl grid place-items-center ${current ? "bg-emerald-50 text-emerald-700" : "bg-slate-50 text-slate-400"}`}>
          <Clock3 className="h-7 w-7" />
        </div>
        <div className="flex-1">
          <p className="ph-label">{current ? "Eingestempelt seit" : "Nicht eingestempelt"}</p>
          <div className="font-display text-5xl font-light tracking-tighter tabular-nums mt-1">
            {current ? elapsedSince(current.start) : "00:00:00"}
          </div>
          {current && (
            <p className="text-xs text-slate-500 mt-1">Gestartet um {fmtTime(current.start)}</p>
          )}
        </div>
        <div className="flex flex-col sm:flex-row gap-2 w-full sm:w-auto">
          {current ? (
            <Button data-testid={DASH.clockOut} onClick={clockOut} size="lg" className="bg-slate-900 hover:bg-slate-800 text-white h-12 px-6">
              <Square className="h-5 w-5 mr-2" /> Ausstempeln
            </Button>
          ) : (
            <Button data-testid={DASH.clockIn} onClick={clockIn} size="lg" className="bg-kauz-navy hover:bg-kauz-navy-dark text-white h-12 px-6">
              <Play className="h-5 w-5 mr-2" /> Einstempeln
            </Button>
          )}
          <ManualEntryDialog
            users={user.role === "admin" ? employees : []}
            defaultUserId={targetId}
            onSaved={reload}
            trigger={
              <Button variant="outline" size="lg" className="h-12 px-6 border-slate-300" data-testid="btn-manual-entry">
                <Plus className="h-5 w-5 mr-2" /> Manuell eintragen
              </Button>
            }
          />
        </div>
      </div>

      {user.role === "admin" && (
        <div className="ph-card p-4 flex items-center gap-3">
          <Label className="text-xs uppercase font-semibold text-slate-600">Mitarbeiter</Label>
          <Select value={targetId} onValueChange={setTargetId}>
            <SelectTrigger data-testid="time-employee-select" className="w-64 h-9"><SelectValue /></SelectTrigger>
            <SelectContent>
              {employees.map((u) => (
                <SelectItem key={u.id} value={u.id}>{u.name} {u.id === user.id && "(ich)"}</SelectItem>
              ))}
            </SelectContent>
          </Select>
          <span className="text-xs text-slate-500 ml-auto">Letzte 60 Tage</span>
        </div>
      )}

      {/* Export panel */}
      <div className="ph-card p-5 md:p-6">
        <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
          <div>
            <h3 className="font-display text-base font-semibold">Stundenkonto-Report</h3>
            <p className="text-xs text-slate-500 mt-1">
              PDF-Report (Soll/Ist/Differenz) oder CSV für die Lohnabrechnung — pro Monat.
            </p>
          </div>
          <div className="flex flex-wrap items-end gap-3">
            <div className="space-y-1">
              <Label className="text-[10px] uppercase tracking-wider font-semibold text-slate-600">Monat</Label>
              <Select value={String(reportMonth)} onValueChange={(v) => setReportMonth(Number(v))}>
                <SelectTrigger data-testid="report-month" className="w-36 h-10"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {MONTHS_DE.map((m, i) => <SelectItem key={i} value={String(i + 1)}>{m}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-1">
              <Label className="text-[10px] uppercase tracking-wider font-semibold text-slate-600">Jahr</Label>
              <Select value={String(reportYear)} onValueChange={(v) => setReportYear(Number(v))}>
                <SelectTrigger data-testid="report-year" className="w-28 h-10"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {[0,1,2,3,4].map((d) => {
                    const y = today.getFullYear() - d;
                    return <SelectItem key={y} value={String(y)}>{y}</SelectItem>;
                  })}
                </SelectContent>
              </Select>
            </div>
            <Button
              data-testid="btn-export-pdf"
              onClick={exportPdf}
              disabled={!!downloading}
              className="bg-kauz-navy hover:bg-kauz-navy-dark h-10"
            >
              <FileText className="h-4 w-4 mr-2" />
              {downloading === "/time/report" ? "Erstelle PDF…" : "PDF-Report"}
            </Button>
            <Button
              data-testid="btn-export-csv"
              onClick={exportCsv}
              disabled={!!downloading}
              variant="outline"
              className="h-10"
            >
              <FileDown className="h-4 w-4 mr-2" />
              {downloading === "/time/export" ? "Lade…" : "CSV"}
            </Button>
          </div>
        </div>
      </div>

      <div className="grid md:grid-cols-3 gap-4">
        <div className="ph-card p-5">
          <p className="ph-label">Buchungen (60 Tage)</p>
          <div className="ph-kpi text-4xl mt-2">{entries.filter(e => e.end).length}</div>
        </div>
        <div className="ph-card p-5">
          <p className="ph-label">Gesamtstunden</p>
          <div className="ph-kpi text-4xl mt-2">{fmtDuration(totalMin)}</div>
        </div>
        <div className="ph-card p-5">
          <p className="ph-label">Ø pro Tag</p>
          <div className="ph-kpi text-4xl mt-2">
            {Object.keys(grouped).length > 0 ? fmtDuration(Math.round(totalMin / Object.keys(grouped).length)) : "—"}
          </div>
        </div>
      </div>

      <div className="ph-card overflow-hidden">
        <div className="px-5 py-3 border-b border-slate-200 bg-slate-50/50 font-medium text-slate-700">Verlauf</div>
        {entries.length === 0 ? (
          <div className="p-12 text-center text-slate-400 text-sm">Noch keine Zeitbuchungen.</div>
        ) : (
          <ul className="divide-y divide-slate-100">
            {Object.entries(grouped).sort(([a],[b]) => (a < b ? 1 : -1)).map(([d, list]) => {
              const dayMin = list.filter(e => e.end).reduce((a, b) => a + (b.duration_minutes || 0), 0);
              return (
                <li key={d} className="px-5 py-3">
                  <div className="flex items-center justify-between mb-2">
                    <div className="font-medium text-slate-800">{fmtDate(d + "T00:00:00")}</div>
                    <div className="text-xs text-slate-500 tabular-nums">{fmtDuration(dayMin)}</div>
                  </div>
                  <ul className="space-y-1">
                    {list.map((e) => (
                      <TimeEntryRow
                        key={e.id}
                        entry={e}
                        users={user.role === "admin" ? employees : []}
                        canEdit={user.role === "admin" || e.user_id === user.id}
                        onSaved={reload}
                      />
                    ))}
                  </ul>
                </li>
              );
            })}
          </ul>
        )}
      </div>
    </div>
  );
}

function TimeEntryRow({ entry, users, canEdit, onSaved }) {
  const remove = async () => {
    if (!window.confirm("Diese Zeitbuchung löschen?")) return;
    try {
      await api.delete(`/time/entries/${entry.id}`);
      toast.success("Buchung gelöscht");
      onSaved();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Fehler");
    }
  };
  return (
    <li className="flex items-center gap-3 text-sm text-slate-600 pl-1 group" data-testid={`time-entry-${entry.id}`}>
      <span className={`h-1.5 w-1.5 rounded-full ${entry.manual ? "bg-violet-500" : "bg-sky-600"}`} />
      <span className="tabular-nums">{fmtTime(entry.start)} → {entry.end ? fmtTime(entry.end) : "…"}</span>
      {entry.manual && (
        <Badge variant="outline" className="text-[9px] uppercase border-violet-200 bg-violet-50 text-violet-800 h-4 px-1.5">
          manuell
        </Badge>
      )}
      {entry.note && <span className="text-xs text-slate-400 italic truncate max-w-[160px]">„{entry.note}"</span>}
      <span className="text-xs text-slate-400 ml-auto tabular-nums">
        {entry.end ? fmtDuration(entry.duration_minutes) : "läuft"}
      </span>
      {canEdit && entry.end && (
        <div className="flex items-center gap-1 opacity-0 group-hover:opacity-100 transition">
          <ManualEntryDialog
            users={users}
            existing={entry}
            onSaved={onSaved}
            trigger={
              <button className="h-7 w-7 grid place-items-center rounded hover:bg-slate-100 text-slate-500"
                data-testid={`time-edit-${entry.id}`} title="Bearbeiten">
                <Pencil className="h-3.5 w-3.5" />
              </button>
            }
          />
          <button
            onClick={remove}
            className="h-7 w-7 grid place-items-center rounded hover:bg-red-50 text-red-600"
            data-testid={`time-delete-${entry.id}`}
            title="Löschen"
          >
            <Trash2 className="h-3.5 w-3.5" />
          </button>
        </div>
      )}
    </li>
  );
}

function ManualEntryDialog({ users, defaultUserId, existing, onSaved, trigger }) {
  const isEdit = !!existing;
  const initialDate = existing ? existing.start.slice(0, 10) : new Date().toISOString().slice(0, 10);
  const initialStart = existing ? existing.start.slice(11, 16) : "09:00";
  const initialEnd = existing && existing.end ? existing.end.slice(11, 16) : "17:00";

  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({
    work_date: initialDate,
    start_time: initialStart,
    end_time: initialEnd,
    note: existing?.note || "",
    user_id: defaultUserId || existing?.user_id || "",
  });
  const [busy, setBusy] = useState(false);

  // Reset when reopening
  useEffect(() => {
    if (open) {
      setForm({
        work_date: existing ? existing.start.slice(0, 10) : new Date().toISOString().slice(0, 10),
        start_time: existing ? existing.start.slice(11, 16) : "09:00",
        end_time: existing && existing.end ? existing.end.slice(11, 16) : "17:00",
        note: existing?.note || "",
        user_id: defaultUserId || existing?.user_id || "",
      });
    }
  }, [open, existing, defaultUserId]);

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      if (isEdit) {
        await api.put(`/time/entries/${existing.id}`, {
          work_date: form.work_date,
          start_time: form.start_time,
          end_time: form.end_time,
          note: form.note,
        });
        toast.success("Buchung aktualisiert");
      } else {
        const payload = {
          work_date: form.work_date,
          start_time: form.start_time,
          end_time: form.end_time,
          note: form.note,
        };
        if (users && users.length > 0 && form.user_id) payload.user_id = form.user_id;
        await api.post("/time/manual", payload);
        toast.success("Zeit eingetragen");
      }
      setOpen(false);
      onSaved();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Fehler beim Speichern");
    } finally {
      setBusy(false);
    }
  };

  const hours = Math.max(0, (toMin(form.end_time) - toMin(form.start_time)) / 60);

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>{trigger}</DialogTrigger>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle className="font-display">
            {isEdit ? "Buchung bearbeiten" : "Arbeitszeit manuell eintragen"}
          </DialogTitle>
        </DialogHeader>
        <form onSubmit={submit} className="space-y-4">
          {!isEdit && users && users.length > 0 && (
            <div className="space-y-1.5">
              <Label className="text-xs uppercase font-semibold text-slate-600">Mitarbeiter</Label>
              <Select value={form.user_id} onValueChange={(v) => setForm({ ...form, user_id: v })}>
                <SelectTrigger data-testid="manual-user"><SelectValue placeholder="Wähle Mitarbeiter" /></SelectTrigger>
                <SelectContent>
                  {users.map((u) => <SelectItem key={u.id} value={u.id}>{u.name}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
          )}
          <div className="space-y-1.5">
            <Label className="text-xs uppercase font-semibold text-slate-600">Datum</Label>
            <Input data-testid="manual-date" type="date" required value={form.work_date}
              onChange={(e) => setForm({ ...form, work_date: e.target.value })} />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-1.5">
              <Label className="text-xs uppercase font-semibold text-slate-600">Von</Label>
              <Input data-testid="manual-start" type="time" required value={form.start_time}
                onChange={(e) => setForm({ ...form, start_time: e.target.value })} />
            </div>
            <div className="space-y-1.5">
              <Label className="text-xs uppercase font-semibold text-slate-600">Bis</Label>
              <Input data-testid="manual-end" type="time" required value={form.end_time}
                onChange={(e) => setForm({ ...form, end_time: e.target.value })} />
            </div>
          </div>
          {hours > 0 && (
            <div className="text-xs text-slate-600 bg-slate-50 border border-slate-200 rounded-md px-3 py-2">
              Arbeitsdauer: <strong>{hours.toFixed(2)} Stunden</strong>
            </div>
          )}
          <div className="space-y-1.5">
            <Label className="text-xs uppercase font-semibold text-slate-600">Notiz (optional)</Label>
            <Textarea value={form.note} placeholder="z.B. Homeoffice, Außentermin Kunde X…"
              onChange={(e) => setForm({ ...form, note: e.target.value })} />
          </div>
          <DialogFooter>
            <Button type="submit" data-testid="manual-submit" disabled={busy} className="bg-kauz-navy hover:bg-kauz-navy-dark">
              {busy ? "Speichern…" : isEdit ? "Speichern" : "Eintragen"}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}

function toMin(t) {
  if (!t) return 0;
  const [h, m] = t.split(":").map(Number);
  return (h || 0) * 60 + (m || 0);
}
